import clsx from "clsx";
import type { CompanyMessageDB } from "~/database/company_messages.model";
import { companyMessageEditSchema } from "~/database/company_messages.model";
import { useEffect, useState } from "react";
import { useFetcher } from "@remix-run/react";
import {
  useForm,
  getFormProps,
  getInputProps,
} from "@conform-to/react";
import { parseWithValibot } from "conform-to-valibot";
import type { action as MessageUpdateAction } from "~/routes/api.company-message.update/route";
import toast from "react-hot-toast";
import ErrorDisplay from "~/components/Common/ErrorDisplay";

interface CompanyMessageProps {
  message: CompanyMessageDB | null;
  isEditable?: boolean;
  userId?: string;
  onEdit?: (
    message: CompanyMessageDB | null,
  ) => void;
}

export default function CompanyMessage({
  message,
  isEditable = false,
  userId,
  onEdit,
}: CompanyMessageProps) {
  const [isEditMode, setIsEditMode] =
    useState(false);
  const fetcher =
    useFetcher<typeof MessageUpdateAction>();

  const [
    form,
    {
      title,
      content,
      author_name,
      author_position,
    },
  ] = useForm({
    onValidate({ formData }) {
      return parseWithValibot(formData, {
        schema: companyMessageEditSchema,
      });
    },
    defaultValue: {
      title: message?.title || "",
      content: message?.content || "",
      author_name: message?.author_name || "",
      author_position:
        message?.author_position || "",
    },
  });
  console.log("fetcher:", fetcher);
  useEffect(() => {
    if (fetcher.data) {
      if (fetcher.data?.success) {
        toast.success("保存しました", {
          duration: 4000,
        });
        setIsEditMode(false);
        onEdit?.(message);
        form.reset();
      }

      !fetcher.data?.success &&
        toast.error("保存に失敗しました", {
          duration: 4000,
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetcher.data,
    fetcher.data?.success,
    onEdit,
    message,
    //form,
  ]);

  const handleCancel = () => {
    form.reset();
    setIsEditMode(false);
  };

  const handleDelete = () => {
    if (!message) return;

    const formData = new FormData();
    formData.append("action", "delete");

    fetcher.submit(formData, {
      method: "post",
      action: "/api/company-message/update",
    });

    onEdit?.(null);
    setIsEditMode(false);
  };

  return (
    <div className={clsx("mt-40")}>
      <fetcher.Form
        {...getFormProps(form)}
        action="/api/company-message/update"
        method="post"
        className={clsx(
          "grid",
          "grid-cols-[auto_1fr_auto]",
          "items-start",
          "max-md:flex",
          "max-md:flex-col",
          "gap-12",
        )}
      >
        {message && (
          <input
            type="hidden"
            name="messageId"
            value={message.id}
          />
        )}
        <input
          type="hidden"
          name="userId"
          value={userId || ""}
        />
        <div
          className={clsx(
            "flex items-center justify-between",
            "w-340",
          )}
        >
          <h3
            className={clsx(
              "text-40 font-bold",
              "max-md:text-24",
            )}
          >
            MESSAGE
          </h3>
        </div>

        {!message && !isEditMode && (
          <div
            className={clsx(
              "text-center",
              "py-40",
              "text-gray-400",
              "text-14",
            )}
          >
            メッセージが登録されていません
          </div>
        )}

        {(message || isEditMode) && (
          <div
            className={clsx(
              "relative",
              isEditMode &&
                "rounded-lg border bg-gray-50 p-20",
            )}
          >
            {isEditMode && message && (
              <button
                onClick={handleDelete}
                type="button"
                className={clsx(
                  "absolute",
                  "top-12",
                  "right-12",
                  "w-24",
                  "h-24",
                  "flex",
                  "items-center",
                  "justify-center",
                  "bg-red-500",
                  "text-white",
                  "rounded-full",
                  "text-14",
                  "hover:bg-red-600",
                  "transition-colors",
                )}
              >
                ×
              </button>
            )}

            {/* タイトル部分 */}
            <div className={clsx("mb-24")}>
              {isEditMode ? (
                <div>
                  <input
                    {...getInputProps(title, {
                      type: "text",
                    })}
                    placeholder="タイトルを入力"
                    className={clsx(
                      "w-full",
                      "text-16",
                      "font-bold",
                      "p-12",
                      "border",
                      "rounded",
                      "focus:outline-none",
                      "focus:border-blue-500",
                      title.errors &&
                        "border-red-500",
                    )}
                  />
                  {title.errors && (
                    <ErrorDisplay
                      error={{
                        title: title.errors,
                      }}
                    />
                  )}
                </div>
              ) : (
                message?.title && (
                  <h4
                    className={clsx(
                      "text-32",
                      "font-bold",
                      "leading-relaxed",
                    )}
                  >
                    {message.title}
                  </h4>
                )
              )}
            </div>

            {/* 本文部分 */}
            <div className={clsx("mb-24")}>
              {isEditMode ? (
                <div>
                  <textarea
                    {...getInputProps(content, {
                      type: "text",
                    })}
                    placeholder="本文を入力"
                    className={clsx(
                      "w-full",
                      "text-14",
                      "p-12",
                      "border",
                      "rounded",
                      "resize-none",
                      "min-h-120",
                      "focus:outline-none",
                      "focus:border-blue-500",
                      content.errors &&
                        "border-red-500",
                    )}
                    rows={8}
                  />
                  {content.errors && (
                    <ErrorDisplay
                      error={{
                        content: content.errors,
                      }}
                    />
                  )}
                </div>
              ) : (
                message?.content && (
                  <div
                    className={clsx(
                      "text-16",
                      "leading-relaxed",
                      "whitespace-pre-wrap",
                    )}
                  >
                    {message.content}
                  </div>
                )
              )}
            </div>

            {/* 作成者情報 */}
            <div
              className={clsx(
                "flex items-start gap-12",
              )}
            >
              {isEditMode ? (
                <>
                  <div>
                    <input
                      {...getInputProps(
                        author_name,
                        { type: "text" },
                      )}
                      placeholder="作成者名"
                      className={clsx(
                        "text-14",
                        "font-bold",
                        "p-10",
                        "border",
                        "rounded",
                        "w-160",
                        "focus:outline-none",
                        "focus:border-blue-500",
                        author_name.errors &&
                          "border-red-500",
                      )}
                    />
                    {author_name.errors && (
                      <ErrorDisplay
                        error={{
                          author_name:
                            author_name.errors,
                        }}
                      />
                    )}
                  </div>
                  <div>
                    <input
                      {...getInputProps(
                        author_position,
                        { type: "text" },
                      )}
                      placeholder="役職"
                      className={clsx(
                        "text-14",
                        "text-gray-600",
                        "p-10",
                        "border",
                        "rounded",
                        "w-120",
                        "focus:outline-none",
                        "focus:border-blue-500",
                        author_position.errors &&
                          "border-red-500",
                      )}
                    />
                    {author_position.errors && (
                      <ErrorDisplay
                        error={{
                          author_position:
                            author_position.errors,
                        }}
                      />
                    )}
                  </div>
                </>
              ) : (
                message?.author_name && (
                  <div
                    className={clsx("text-20")}
                  >
                    <span
                      className={clsx(
                        "font-bold",
                      )}
                    >
                      {message.author_name}
                    </span>
                    {message.author_position && (
                      <span
                        className={clsx(
                          "text-12",
                          "text-gray-600",
                          "ml-4",
                        )}
                      >
                        {message.author_position}
                      </span>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {isEditMode && (
          <div
            className={clsx(
              "flex",
              "gap-12",
              "justify-end",
            )}
          >
            <button
              onClick={handleCancel}
              className={clsx(
                "px-20",
                "py-8",
                "border",
                "rounded-full",
                "text-12",
              )}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className={clsx(
                "px-20",
                "py-8",
                "bg-black",
                "text-white",
                "rounded-full",
                "text-12",
              )}
            >
              保存
            </button>
          </div>
        )}

        {isEditable && !isEditMode && (
          <div
            className={clsx(
              "w-340",
              "text-right",
            )}
          >
            <button
              onClick={() => setIsEditMode(true)}
              className={clsx(
                "text-12",
                "px-16",
                "py-6",
                "border",
                "rounded-full",
                "hover:bg-gray-50",
              )}
            >
              {message ? "編集" : "追加"}
            </button>
          </div>
        )}
      </fetcher.Form>
    </div>
  );
}
